import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

// Mirror the JWT into a same-origin cookie so <img src="/api/media/files/..."> requests
// (which cannot carry an Authorization header) authenticate owner-only files.
function setAuthCookie(token) {
  // 30 days
  const maxAge = 60 * 60 * 24 * 30;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${TOKEN_KEY}=${token}; path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}
function clearAuthCookie() {
  document.cookie = `${TOKEN_KEY}=; path=/; Max-Age=0; SameSite=Lax`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const [initializing, setInitializing] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      clearAuthCookie();
      setUser(false);
      setInitializing(false);
      return;
    }
    setAuthCookie(token);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (_e) {
      localStorage.removeItem(TOKEN_KEY);
      clearAuthCookie();
      setUser(false);
    } finally {
      setInitializing(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setAuthCookie(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      localStorage.setItem(TOKEN_KEY, data.token);
      setAuthCookie(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    clearAuthCookie();
    // Master Order caches its resolved access mode for the current
    // session so page navigation stays smooth on transient errors.
    // Wipe it on logout so the next signed-in user starts fresh.
    try { sessionStorage.removeItem("anokina_mo_mode"); } catch { /* ignore */ }
    setUser(false);
  };

  // Refresh the current user from /auth/me (used after profile edits like avatar upload).
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (_e) {
      return null;
    }
  }, []);

  const updateInterfaceLocale = useCallback(async (locale) => {
    const { data } = await api.patch("/localization/locale", { locale });
    setUser((current) => (
      current && typeof current === "object"
        ? { ...current, locale: data.locale }
        : current
    ));
    return data.locale;
  }, []);

  return (
    <AuthContext.Provider value={{ user, initializing, login, register, logout, refreshUser, updateInterfaceLocale }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
